/**
 * A shelf — an ordered list of ROWS with a picker on its header.
 *
 * Rows rather than inline pills: every entry carries controls of its own — a
 * sort direction, a display option, a share mode — and a pill wide enough to
 * hold them is not a pill. A row gives the label the left, the controls the
 * right, and every shelf the same shape.
 *
 * Nesting order is what a pivot MEANS, so it is explicit, numbered and
 * reorderable. The whole row is the drag handle; the keyboard path is
 * Alt+↑ / Alt+↓ from the grip, because a shelf that can only be reordered by
 * dragging is unusable without a mouse and this ships in a library whose
 * consumers we do not control.
 */
import { FormulaHint } from '@clockwise/ui';
import type { ReactNode } from 'react';
import type { DragPayload } from './drag';

export interface ShelfRow {
  /** Identity, and the React key. */
  id: string;
  label: string;
  /** Shown under the label — a sort's scope, whatever the shelf means. */
  detail?: string;
  /** A measure's formula: an ƒ icon beside the label, shown on hover. */
  formula?: string;
  /** This row's own controls, rendered to the right of the label. */
  controls?: ReactNode;
  /**
   * A block of controls on a LINE OF ITS OWN, under the label.
   *
   * For what does not fit beside a name and should not be behind a click: a
   * pivot field's order is four widgets, and a report whose row order can only
   * be read by opening four popovers is a report nobody reads the order of.
   */
  settings?: ReactNode;
  /**
   * Accessible name for the remove button. Defaults to `Remove <label>`, which
   * is ambiguous when the same thing sits on two shelves — a measure can be
   * both a value and a sort key.
   */
  removeLabel?: string;
}

interface Props {
  /** Distinguishes drop zones, and names the test id. */
  axis: string;
  /** Omit when the section heading above already names this shelf. */
  title?: string;
  /**
   * THE EMPTY STATE, and nothing else.
   *
   * It used to render above the shelf as well, which printed it twice on the
   * shelf it exists for — the empty one. A populated shelf is explained by the
   * rows in it and by the heading above them; the prompt is only worth the
   * line while there is nothing there to read instead.
   */
  hint?: string;
  rows: readonly ShelfRow[];
  /** The picker rendered on the shelf header. */
  add?: ReactNode;
  onReorder: (from: number, to: number) => void;
  onRemove: (id: string) => void;
  /** Called when a chip is dropped here from anywhere. */
  onDrop: (payload: DragPayload, index: number | undefined) => void;
  drag: DragPayload | null;
  setDrag: (payload: DragPayload | null) => void;
  dropIndex: number | null;
  setDropIndex: (index: number | null) => void;
  activeZone: string | null;
  setActiveZone: (axis: string | null) => void;
}

export function Shelf({
  axis,
  title,
  hint,
  rows,
  add,
  onReorder,
  onRemove,
  onDrop,
  drag,
  setDrag,
  dropIndex,
  setDropIndex,
  activeZone,
  setActiveZone,
}: Props) {
  const finishDrag = () => {
    setDrag(null);
    setDropIndex(null);
    setActiveZone(null);
  };

  return (
    <div className="rk-shelf" data-testid={`shelf-${axis}`}>
      {/* Both texts are optional. A shelf that is the only one under its
          numbered heading has already been named by that heading, and repeating
          it — COLUMNS above Columns above "left to right" — is three labels for
          one control. */}
      <div className="rk-shelf-head">
        {title && <span className="rk-shelf-title">{title}</span>}
        {add}
      </div>
      {/* Drag is a pointer-only enhancement: no interactive ARIA role fits a
          drop target, and giving it one would announce a control keyboard
          users cannot operate. Reordering and removal are both reachable from
          the chip itself. */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: drop target; the keyboard path is on the chips */}
      <div
        className={[
          'rk-dropzone',
          // An empty shelf is not a box. A dashed grey rectangle holding
          // nothing reads as a broken control; the Add on the heading is the
          // affordance. `armed` brings it back the instant a drag starts,
          // because then it genuinely IS a target and has to be aimed at.
          rows.length === 0 ? 'rk-dropzone-empty' : '',
          drag ? 'rk-dropzone-armed' : '',
          activeZone === axis ? 'rk-dropzone-over' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setActiveZone(axis);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setActiveZone(null);
            setDropIndex(null);
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          if (drag) onDrop(drag, dropIndex ?? undefined);
          finishDrag();
        }}
        data-testid={`dropzone-${axis}`}
      >
        {rows.length === 0 && <span className="rk-placeholder">{hint}</span>}

        {rows.map((row, index) => (
          // The chip is a drag SOURCE and a keyboard target. Its affordances
          // are the buttons inside it; making the wrapper a button too would
          // add a second, redundant stop in the tab order.
          // biome-ignore lint/a11y/noStaticElementInteractions: drag source; controls are the nested buttons
          <span
            key={row.id}
            className={`rk-row${drag?.dimensionId === row.id ? ' rk-row-dragging' : ''}${
              activeZone === axis && dropIndex === index ? ' rk-row-insert' : ''
            }`}
            draggable
            onDragStart={(e) => {
              // The whole row drags, which means a press that lands on one of
              // its buttons would start a drag instead of clicking it — the
              // control becomes unusable with any slight movement. A press
              // that starts on a control is a press, not a drag.
              //
              // `[role="dialog"]` covers an OVERLAY a row hosts — the measure
              // shelf's format popup is rendered through `settings`, so it is
              // a DOM descendant of this draggable row even though it floats.
              // Its negative-numbers list is `div[role="option"]` and its
              // sample box is a plain div, so without this a press on either
              // dragged the measure out from under the pointer, and dropping
              // it reordered the shelf. Matching the dialog rather than listing
              // its innards means the next control added inside it is covered.
              const onControl = (e.target as HTMLElement).closest(
                'button, select, input, label, [role="dialog"]',
              );
              if (onControl && !onControl.hasAttribute('data-drag-handle')) {
                e.preventDefault();
                return;
              }
              setDrag({ from: axis, dimensionId: row.id });
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', row.id);
            }}
            onDragEnd={finishDrag}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Rows stack vertically, so the midpoint that matters is
              // horizontal.
              const rect = e.currentTarget.getBoundingClientRect();
              setDropIndex(e.clientY < rect.top + rect.height / 2 ? index : index + 1);
              setActiveZone(axis);
            }}
          >
            {/* The grip is a real button, not a focusable span: it is the
                visual affordance for dragging, and making IT the keyboard
                target gives the shelf an announceable, tab-reachable control
                instead of an element screen readers cannot classify. */}
            <button
              type="button"
              className="rk-grip"
              // Excluded from the guard above: the grip is the drag affordance.
              data-drag-handle="true"
              aria-label={`${row.label}, position ${index + 1} of ${rows.length}. Alt with up or down arrow to move.`}
              onKeyDown={(e) => {
                // Alt, not bare arrows: bare arrows are how a screen-reader
                // user moves between elements, and stealing them would make
                // the shelf impossible to read.
                if (!e.altKey) return;
                if (e.key === 'ArrowUp' && index > 0) {
                  e.preventDefault();
                  onReorder(index, index - 1);
                } else if (e.key === 'ArrowDown' && index < rows.length - 1) {
                  e.preventDefault();
                  onReorder(index, index + 1);
                }
              }}
            >
              <span aria-hidden="true">⠿</span>
            </button>
            <span className="rk-row-order">{index + 1}</span>
            <span className="rk-row-body">
              {/* Titled, because it clips: a name longer than the row is cut
                  with an ellipsis and the tooltip is the only way to read the
                  rest of it. */}
              <span className="rk-row-head">
                <span className="rk-row-label" title={row.label}>
                  {row.label}
                </span>
                {row.formula && <FormulaHint formula={row.formula} label={row.label} focusable />}
              </span>
              {row.detail && <span className="rk-row-detail">{row.detail}</span>}
            </span>

            {/* Controls sit outside the label, on the right of the row, so a
                direction or a display option is a widget in its own right
                rather than something squeezed into a badge.

                ONE ELEMENT around the widgets AND the remove button, because
                the row wraps: as two flex items they wrapped independently, so
                a row whose name and widgets just fitted sent the ✕ down alone
                to the far left of a second line, under the drag grip. They are
                the right-hand end of the row and they travel as one. */}
            <span className="rk-row-actions">
              {row.controls && <span className="rk-row-controls">{row.controls}</span>}
              <button
                type="button"
                className="rk-icon"
                title="Remove"
                aria-label={row.removeLabel ?? `Remove ${row.label}`}
                onClick={() => onRemove(row.id)}
              >
                ✕
              </button>
            </span>

            {/* Last, and full width, so the wrap that `.rk-row` already does
                puts it on its own line under everything above. A block inside
                the row rather than a sibling of it: the whole row is the drag
                source and the drop target, and settings that sat outside it
                would be a strip you could drop between. */}
            {row.settings && <span className="rk-row-settings">{row.settings}</span>}
          </span>
        ))}
      </div>
    </div>
  );
}
